import { Injector, Signal, WritableSignal, inject, isSignal, linkedSignal } from '@angular/core';
import { FieldTree, SchemaPath, SchemaPathTree, disabled, email, form, max, maxLength, min, minLength, pattern, required, validate } from '@angular/forms/signals';
import { UtilityService } from '../../../services/util';
import { AddressFieldData, AngularField, AngularFieldKind, CommonValidations, NumberValidations, TextValidations } from './angular-form-fields';

const PHONE_PATTERN = /^(?:\+1)?\s?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

type AngularValidations = Partial<TextValidations & NumberValidations & CommonValidations>;

export function getSignalValue<T>(value: T | Signal<T>): T {
	return isSignal(value) ? value() : value;
}

export class AngularCommonForm<VALUES = Record<string, unknown>> {

	#util = inject(UtilityService);

	constructor(private readonly injector: Injector) { }

	private readonly fields: {
		formFieldName: keyof VALUES & string;
		initialValue: () => unknown;
		applyValidations: (root: SchemaPathTree<VALUES>) => void;
		assignFormField: (formTree: FieldTree<VALUES>) => void;
	}[] = [];

	private builtForm: FieldTree<VALUES> | undefined;

	readonly model: WritableSignal<VALUES> = linkedSignal<VALUES>(() => this.getInitialValues());

	// Get the initial values for the form based on the configured fields.
	// Angular signal forms treat `undefined` as "key not present".
	// We store `null` for undefined values so every configured field exists in the model.
	private getInitialValues(): VALUES {
		return Object.fromEntries(
			this.fields.map(field => [
				field.formFieldName,
				field.initialValue() ?? null
			])
		) as VALUES;
	}


	/**
	 * Add a field to the form configuration. This method should be called before `build()`.
	 * @template KEY The key of the field in the VALUES type.
	 * @template FIELD The type of the AngularField that is needed as the form field.
	 * @param field The field configuration to add.
	 * @returns The added AngularField instance.
	 */
	addField<
		KEY extends keyof VALUES & string,
		FIELD extends AngularField<VALUES[KEY], AngularValidations>
	>(field: Omit<FIELD, 'formField'> & { formFieldName: KEY }): FIELD {

		if (this.builtForm) { throw new Error(`Cannot add "${field.formFieldName}" after the form has been built.`); }

		const alreadyExists = this.fields.some(current => current.formFieldName === field.formFieldName);
		if (alreadyExists) { throw new Error(`A field named "${field.formFieldName}" already exists.`); }

		const angularField = {
			...field,
			formField: undefined! // gets assigned in build() function after form tree is created
		} as unknown as FIELD;

		this.fields.push({
			formFieldName: field.formFieldName,
			initialValue: field.initialValue,

			applyValidations: root => {
				const fields = root as Record<KEY, SchemaPath<VALUES[KEY]>>;
				const fieldPath = fields[field.formFieldName];
				this.addValidations(fieldPath, field.kind, field.label, field.validations);
			},

			assignFormField: formTree => {
				const tree = formTree as Record<KEY, FieldTree<VALUES[KEY]>>;
				angularField.formField = tree[field.formFieldName];
			}
		});

		return angularField;
	}

	/**
	 * Build the form based on the added fields and their validations.
	 * This method should be called after all fields have been added.
	 */
	build(): void {
		if (this.builtForm) {
			return;
		}

		this.model.set(this.getInitialValues());

		const formTree = form(this.model, root => {
			for (const field of this.fields) {
				field.applyValidations(root);
			}
		}, { injector: this.injector });

		for (const field of this.fields) {
			field.assignFormField(formTree);
		}

		this.builtForm = formTree;
	}

	// Add validations to a field based on its kind and the provided validation rules.
	private addValidations<VALUE>(fieldPath: SchemaPath<VALUE>, kind: AngularFieldKind, label: string | Signal<string>, validations?: AngularValidations): void {

		const strPath = fieldPath as SchemaPath<string>; // inbuilt validators internally check for null, so we can safely cast to string
		const numPath = fieldPath as SchemaPath<number | null>;

		const displayLabel = getSignalValue(label);

		if (!validations) return;

		if (validations.isDisabled) {
			disabled(fieldPath, {
				when: () => validations.isDisabled?.() ?? false
			});
		}

		required(fieldPath, {
			when: () => validations.isRequired?.() ?? false,
			message: `${displayLabel} is required`
		});

		// add null checks for minLength, maxLength, pattern, min, max, isEmail, isPhone
		if (validations.minLength != undefined) {
			minLength(strPath, validations.minLength, { message: `${displayLabel} is too short` });
		}

		if (validations.maxLength != undefined) {
			maxLength(strPath, validations.maxLength, { message: `${displayLabel} is too long` });
		}

		if (validations.pattern != undefined) {
			pattern(strPath, validations.pattern, { message: `${displayLabel} has an invalid format` });
		}

		if (validations.min != undefined) {
			min(numPath, validations.min, { message: `${displayLabel} must be at least ${validations.min}` });
		}

		if (validations.max != undefined) {
			max(numPath, validations.max, { message: `${displayLabel} cannot exceed ${validations.max}` });
		}

		if (validations.isEmail) {
			email(strPath, { message: `Enter a valid ${displayLabel.toLowerCase()}` });
		}

		if (validations.isPhone) {
			pattern(strPath, PHONE_PATTERN, { message: `Enter a valid ${displayLabel.toLowerCase()}` });
		}

		if (validations.isURL) {
			validate(strPath, ({ value }) => {
				const val = value();

				if (!val) return null;

				return this.#util.text.isValidUrl(val) ? null : { kind: 'isUrl', message: `Enter a valid ${displayLabel.toLowerCase()} URL` };
			});
		}

		if (kind === 'address') {
			const addressPath = fieldPath as SchemaPath<AddressFieldData | null>;
			validate(addressPath, ({ value }) => {
				const val = value();

				// If empty, standard required() rule handles it
				if (!val) return null;

				if (!val.placeAddress || !val.placeAddress.trim()) {
					return {
						kind: 'invalidAddress',
						message: `Please enter a valid address`
					};
				}

				// User typed something but didn't select an option from autocomplete
				if (val.noSelection) {
					return {
						kind: 'noSelection',
						message: `Please select a valid address from the suggestions`
					};
				}

				// Check complete mailing address (Street Address & ZIP ID)
				if (!val.zipId || !val.streetAddress) {
					return {
						kind: 'invalidMailingAddress',
						message: `Please enter a valid US mailing address`
					};
				}

				// Check County FIPS or State Code
				if (!val.countyFipsId && !val.stateCode) {
					return {
						kind: 'invalidCountyFipsId',
						message: `Invalid Address field: Please update your address and try again`
					};
				}

				return null; // Valid address
			});

		}
	}

	private get angularForm(): FieldTree<VALUES> {

		if (!this.builtForm) { throw new Error('AngularCommonForm.build() must be called first.'); }

		return this.builtForm;
	}

	// The current values of the form.If a field is empty, its value will be `undefined` instead of `null` or an empty string.
	value(): VALUES {
		if (!this.builtForm) { throw new Error('AngularCommonForm.build() must be called first.'); }

		const values = {} as Record<string, unknown>;

		for (const [key, value] of Object.entries(this.builtForm().value() as Record<string, unknown>)) {
			values[key] = value === null || value === '' ? undefined : value;
		}

		return values as VALUES;
	}

	// Reset the form to its initial values
	reset(): void {
		if (!this.builtForm) { throw new Error('AngularCommonForm.build() must be called first.'); }

		this.model.set(this.getInitialValues());
		this.builtForm().reset();
	}

	// `true` if the form is valid, `false` otherwise. This method also marks all fields as touched to trigger validation messages.
	touchAndValidate(): boolean {
		if (!this.builtForm) { throw new Error('AngularCommonForm.build() must be called first.'); }

		this.builtForm().markAsTouched();
		return this.builtForm().valid();
	}

	isValid(): boolean {
		if (!this.builtForm) { throw new Error('AngularCommonForm.build() must be called first.'); }
		return this.builtForm().valid();
	}
}
